<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('customer.profile') }}
        </span>
        <h3 class="page-title">
          {{ $t('customer.yourAccount') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="!isUser"
          theme="success"
          @click.prevent="validateForm">
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="customer-edit-form" tag="form" @keydown.enter.prevent>
        <d-card class="mb-5">
          <d-tabs
            :nav-class="allDataLoaded ? validationResults : ''"
            card
            v-if="!loadError">
            <!-- Dane ogólne -->
            <d-tab
              v-if="!isUser"
              :title="$t('ui.generalInfo')"
              :active="!isUser">
              <h4 class="mb-3 pb-3 border-bottom d-flex">
                <span class="mr-auto">{{ $t('ui.generalInfo') }}</span>
              </h4>

              <d-row>
                <d-col :cols="12">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.companyName') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col :cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.language') }}:
                    </label>

                    <div :class="{
                      'form-control': true,
                      'is-select': true
                    }">
                      <v-select
                        :options="languageOptions"
                        v-model="language_id"
                        :searchable="false"
                        :clearable="false"
                        :reduce="language => language.value"
                        :placeholder="$t('settings.language.chooseLanguage')"
                        label="text">
                        <template slot="no-options">
                          {{ $t('settings.language.noLanguagesAvailable') }}&hellip;
                        </template>
                      </v-select>
                    </div>
                  </div>
                </d-col>

                <d-col :cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.currency.currency') }}:
                    </label>

                    <span class="text-primary">
                      {{ currencyName }}
                    </span>
                  </div>
                </d-col>

                <d-col :cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.shipper') }}:
                    </label>

                    <span class="text-primary">
                      {{ shipperName }}
                    </span>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.country.country') }}:
                    </label>

                    <span class="text-primary">
                      {{ countryName }}
                    </span>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupProducts') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_products"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_products"
                          type="text"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupServices') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_services"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_services"
                          type="text"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupSetupServices') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_setup_services"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_setup_services"
                          type="text"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupShipping') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_shipping"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_shipping"
                          type="text"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label>
                  {{ $t('customer.customerLogo') }}:
                </label>

                <div v-if="logo_url" class="customer-logo">
                  <img :src="logo_url" />
                </div>

                <div v-if="logo_url">
                  <a
                    href="#"
                    @click.prevent="removeCustomerLogo()"
                    class="text-danger">
                    <small>{{ $t('customer.removeLogo') }}</small>
                  </a>
                </div>

                <file-input
                  v-if="!logo_url"
                  :onChange="setCustomerLogo"
                  id="inputFileCustomerLogo" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.emailAddress') }}:
                </label>

                <validation-provider
                  rules="email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="email"
                    v-model="email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('customer.telephone') }}:
                </label>

                <d-input
                  v-mask="'+## ### ### ### ### ### ### ### ### ###'"
                  v-model="phone" />
              </div>
            </d-tab>

            <d-tab
              v-if="!isUser"
              :title="$t('customer.invoiceInfo')">
              <table class="table">
                <tr>
                  <td>
                    {{ $t('settings.country.countryCode') }}:
                  </td>
                  <td>
                    <span class="text-primary">
                      {{ invoice_country_prefix }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.vatIDNumber') }}:
                  </td>
                  <td>
                    <span class="text-primary">
                      {{ invoice_tax_number }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.invoiceName') }}
                  </td>
                  <td>
                    <span class="text-primary">
                      {{ invoice_name }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.city') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ invoice_city }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.zipCode') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ invoice_postcode }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.street') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ invoice_street }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.buildingNumber') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ invoice_building }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.houseNumber') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ invoice_locale }}
                    </span>
                  </td>
                </tr>
              </table>
            </d-tab>

            <d-tab
              v-if="!isUser"
              :title="$t('customer.correspondenceInfo')">
              <table class="table">
                <tr>
                  <td>
                    {{ $t('settings.country.country') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ correspondenceCountryName }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('ui.nameAndSurename') }}:
                  </td>
                  <td>
                    <span class="text-primary">
                      {{ correspondence_name }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('ui.companyName') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ correspondence_company_name }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.city') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ correspondence_city }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.zipCode') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ correspondence_postcode }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.street') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ correspondence_street }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.buildingNumber') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ correspondence_building }}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td>
                    {{ $t('customer.houseNumber') }}:
                  </td>

                  <td>
                    <span class="text-primary">
                      {{ correspondence_locale }}
                    </span>
                  </td>
                </tr>
              </table>
            </d-tab>

            <d-tab
              v-if="!isUser"
              :title="$t('customer.shippingInfo')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('customer.shippingInfo') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addAddress">
                  <i class="material-icons">add</i>
                  {{ $t('customer.addAddress') }}
                </d-button>
              </h4>

              <d-row class="px-3 pb-4">
                <div class="d-flex w-100">
                  <d-input
                    v-model="addressFilter"
                    :placeholder="$t('customer.searchAddressesByCompanyNameAddressOrContactInfo')" />
                  <d-button
                    v-if="addressFilter"
                    @click.prevent="addressFilter = ''"
                    theme="danger"
                    outline
                    class="ml-2">
                    {{ $t('welcome.clear') }}
                  </d-button>
                </div>
                <small class="w-100 pt-2">
                  {{ $t('customer.fountAddresses') }} <strong>{{ filteredAddresses.length }}</strong>.<br>
                  {{ $t('customer.maxAddressSearchResults') }}
                </small>
              </d-row>

              <table class="table mt-0 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('customer.company') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.address') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.person') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="loadedAddresses">
                  <tr
                    v-for="(address, index) of filteredAddresses"
                    :key="'address-' + index">
                    <td class="align-middle">
                      {{ address.company_name }}
                    </td>
                    <td class="align-middle">
                      {{ getCountry(address.country_id) }}<br>
                      {{ address.street }} <template v-if="address.building">{{ address.building }} / {{ address.locale }}</template><br>
                      {{ address.postcode }} {{ address.city }}
                    </td>
                    <td class="align-middle">
                      {{ address.name }}<br>
                      {{ address.email }}<br>
                      {{ address.phone }}
                    </td>
                    <td
                      class="align-middle"
                      style="width: 200px;">
                      <d-button
                        theme="primary"
                        @click.prevent="editAddress(address)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        @click.prevent="removeAddress(address.id)"
                        class="mr-2"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                  <tr v-if="addresses.length === 0">
                    <td
                      colspan="4"
                      class="text-center">
                      {{ $t('customer.noAddressesToDisplay') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="!loadedAddresses" class="pb-3 pt-3 text-center">
                {{ $t('ui.dataLoading') }}&hellip;
              </div>

              <d-alert
                v-if="loadAddressesError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
            </d-tab>

            <d-tab
              v-if="!isUser"
              :title="$t('customer.contactPeople')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('customer.contactPeople') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addContactPerson">
                  <i class="material-icons">add</i>
                  {{ $t('customer.addContactPerson') }}
                </d-button>
              </h4>

              <table class="table mt-0 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('ui.nameAndSurename') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.emailAddress') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.telephone') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.stockReportsNotifications') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.newsletterAgreement') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="loadedContactPersons">
                  <tr
                    v-for="(contactPerson, index) of contactPersons"
                    :key="'contact-person-' + index">
                    <td class="align-middle">
                      {{ contactPerson.name }}
                    </td>
                    <td class="align-middle">
                      {{ contactPerson.email }}
                    </td>
                    <td class="align-middle">
                      {{ contactPerson.phone }}
                    </td>
                    <td class="align-middle">
                      <span
                        v-if="contactPerson.exclude_from_stock_report"
                        class="text-danger">
                        {{ $t('ui.no') }}
                      </span>
                      <span
                        v-else
                        class="text-success">
                        {{ $t('ui.yes') }}
                      </span>
                    </td>
                    <td class="align-middle">
                      <span
                        v-if="contactPerson.fresh_mail_integrated_at"
                        class="text-success">
                        {{ contactPerson.fresh_mail_integrated_at }}
                      </span>
                      <span
                        v-else
                        class="text-danger">
                        {{ $t('ui.lack') }}
                      </span>
                    </td>
                    <td
                      class="align-middle"
                      style="width: 200px;">
                      <d-button
                        theme="primary"
                        @click.prevent="editContactPerson(contactPerson)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        @click.prevent="removeContactPerson(contactPerson.id)"
                        class="mr-2"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                  <tr v-if="contactPersons.length === 0">
                    <td
                      colspan="6"
                      class="text-center">
                      {{ $t('customer.noContactPeopleToDisplay') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="!loadedContactPersons" class="pb-3 pt-3 text-center">
                {{ $t('ui.dataLoading') }}&hellip;
              </div>

              <d-alert
                v-if="loadContactPersonsError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
            </d-tab>

            <d-tab
              :title="$t('customer.passowrdChange')"
              :active="isUser">
              <h4 class="pb-3 mb-4 border-bottom d-flex">
                {{ $t('customer.changeYourPassword') }}
              </h4>

              <validation-observer ref="customer-password-change" tag="form" @keydown.enter.prevent>
                <div class="form-group">
                  <label for="email">
                    {{ $t('login.password') }}:
                  </label>

                  <validation-provider
                    rules="min:6"
                    name="password"
                    v-slot="{ errors }">
                    <d-input
                      type="password"
                      :state="errors.length ? 'invalid' : null"
                      v-model="password" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <label for="email">
                    {{ $t('ui.repeatPassword') }}:
                  </label>

                  <validation-provider
                    rules="password:password"
                    name="password-confirmation"
                    v-slot="{ errors }">
                    <d-input
                      type="password"
                      :state="errors.length ? 'invalid' : null"
                      v-model="passwordConfirmation" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <d-button
                    theme="success"
                    outline
                    :disabled="passwordChangeInProgress"
                    @click.prevent="validatePasswordChangeForm">
                    <template v-if="!passwordChangeInProgress">{{ $t('customer.changePassword') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </d-button>
                </div>
              </validation-observer>
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center  overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <customer-address-new-popup
      v-if="showAddressAddPopup"
      mode="partner"
      :countries="countries"
      :customerID="this.id" />

    <customer-address-edit-popup
      v-if="showAddressEditPopup"
      mode="partner"
      :customerID="this.id"
      :countries="countries"
      :addressData="selectedAddress" />

    <customer-contact-person-new-popup
      v-if="showContactPersonAddPopup"
      mode="partner"
      :customerID="this.id" />

    <customer-contact-person-edit-popup
      v-if="showContactPersonEditPopup"
      mode="partner"
      :customerID="this.id"
      :contactPersonData="selectedContactPerson" />
  </d-container>
</template>

<script>
import CommonGetters from './../utils/CommonGetters.js';
import CustomerAddressNewPopup from './../components/customers/CustomerAddressNewPopup.vue';
import CustomerAddressEditPopup from './../components/customers/CustomerAddressEditPopup.vue';
import CustomerContactPersonNewPopup from './../components/customers/CustomerContactPersonNewPopup.vue';
import CustomerContactPersonEditPopup from './../components/customers/CustomerContactPersonEditPopup.vue';
import FormUtils from './../utils/FormUtils.js';
import ListUtils from './../utils/ListUtils.js';
import { mapGetters } from 'vuex';
import Vue from 'vue';

export default {
  name: 'customer-edit',
  components: {
    'customer-address-new-popup': CustomerAddressNewPopup,
    'customer-address-edit-popup': CustomerAddressEditPopup,
    'customer-contact-person-new-popup': CustomerContactPersonNewPopup,
    'customer-contact-person-edit-popup': CustomerContactPersonEditPopup
  },
  computed: {
    ...mapGetters([
      'getUserID'
    ]),
    allDataLoaded () {
      return this.isUser || (this.loadedData && this.loadedCountries && this.loadedLanguages && this.loadedCurrencies && this.loadedShippers);
    },
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    },
    countriesCodesOptions () {
      return [{ value: '', text: this.$t('settings.country.selectCountry') }].concat(CommonGetters.getCountriesCodesForSelect(this.countries));
    },
    validationResults () {
      if (this.isUser) {
        return '';
      }

      let errors = this.$refs['customer-edit-form'].errors;
      let errorTabs = [];
      let results = [
        ['error-1', ['name', 'phone', 'email', 'country-id', 'shipper_id', 'markup_products', 'markup_services', 'markup_setup_services', 'markup_shipping']],
        ['error-2', ['correspondence-country-id', 'correspondence-company-name', 'correspondence-city', 'correspondence-postcode', 'correspondence-street']]
      ];

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errors[key] && errors[key].length) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.join(' ');
    },
    languageOptions () {
      return this.languages.map(language => ({
        value: language.id,
        text: language.name + ' (' + language.code + ')'
      }));
    },
    currencyOptions () {
      return this.currencies.map(currency => ({
        value: currency.id,
        text: currency.code
      }));
    },
    shipperOptions () {
      return this.shippers.map(shipper => ({
        value: shipper.id,
        text: shipper.name
      }));
    },
    filteredAddresses () {
      if (!this.addressFilter) {
        return this.addresses.slice(0, 50);
      }

      return this.addresses.filter(address => ([address.company_name, address.street, address.building, address.locale, address.postcode, address.city, address.name, address.email, address.phone].join(' ')).toLocaleLowerCase().indexOf(this.addressFilter) > -1).slice(0, 50);
    },
    customerID () {
      return parseInt(this.getUserID, 10);
    },
    countryName () {
      let foundedCountry = this.countries.filter(country => country.id === this.country_id);

      if (foundedCountry[0]) {
        return foundedCountry[0].name;
      }

      return '??';
    },
    correspondenceCountryName () {
      let foundedCountry = this.countries.filter(country => country.id === this.correspondence_country_id);

      if (foundedCountry[0]) {
        return foundedCountry[0].name;
      }

      return '??';
    },
    currencyName () {
      let foundedCurrency = this.currencies.filter(currency => currency.id === this.currency_id);

      if (foundedCurrency[0]) {
        return foundedCurrency[0].name;
      }

      return '??';
    },
    languageName () {
      let foundedLanguage = this.languages.filter(language => language.id === this.language_id);

      if (foundedLanguage[0]) {
        return foundedLanguage[0].name;
      }

      return '??';
    },
    shipperName () {
      let foundedShipper = this.shippers.filter(shipper => shipper.id === this.shipper_id);

      if (foundedShipper[0]) {
        return foundedShipper[0].name;
      }

      return '??';
    },
    languageCode () {
      if (this.languages) {
        let foundedLang = this.languages.filter(lang => lang.id === this.language_id);

        if (foundedLang[0]) {
          return foundedLang[0].code.toLocaleLowerCase();
        }

        return 'pl';
      }

      return 'pl';
    },
    isUser () {
      return !!this.$store.getters.getParentID;
    }
  },
  watch: {
    allDataLoaded (newValue) {
      if (newValue) {
        if (this.countries.length) {
          this.correspondenceCountryID = this.countries[0].id;
        }
      }
    }
  },
  data() {
    return {
      id: 0,
      name: '',
      email: '',
      phone: '',
      country_id: null,
      logo: '',
      logo_url: '',
      currency_id: null,
      language_id: null,
      shipper_id: null,
      correspondence_country_id: 0,
      correspondence_name: '',
      correspondence_company_name: '',
      correspondence_street: '',
      correspondence_building: '',
      correspondence_locale: '',
      correspondence_postcode: '',
      correspondence_city: '',
      invoice_receiver: null,
      invoice_name: '',
      invoice_email: '',
      invoice_street: '',
      invoice_building: '',
      invoice_locale: '',
      invoice_postcode: '',
      invoice_city: '',
      invoice_tax_number: '',
      invoice_country_prefix: '',
      markup_products: 0,
      markup_services: 0,
      markup_setup_services: 0,
      markup_shipping: 0,
      loadedData: false,
      loadedCountries: false,
      loadedLanguages: false,
      loadedCurrencies: false,
      loadedShippers: false,
      countries: [],
      contactPersons: [],
      addresses: [],
      languages: [],
      currencies: [],
      shippers: [],
      loadError: false,
      loadedAddresses: false,
      loadAddressesError: false,
      showAddressAddPopup: false,
      showAddressEditPopup: false,
      selectedAddress: false,
      loadedContactPersons: false,
      loadContactPersonsError: false,
      showContactPersonAddPopup: false,
      showContactPersonEditPopup: false,
      selectedContactPerson: false,
      removeAddressInProgress: false,
      removeContactPersonInProgress: false,
      password: '',
      passwordConfirmation: '',
      passwordChangeInProgress: false,
      addressFilter: ''
    };
  },
  mounted () {
    if (this.isUser) {
      return;
    }

    let idToLoad = 0;
    let endpointUrl = '/customer-panel/customers/item';

    FormUtils.loadItemData(this, {
      endpointBase: endpointUrl,
      instanceFields: ['name', 'email', 'phone', 'country_id', 'logo', 'logo_url', 'language_id', 'currency_id', 'shipper_id', 'correspondence_country_id', 'correspondence_name', 'correspondence_company_name', 'correspondence_street', 'correspondence_building', 'correspondence_locale', 'correspondence_postcode', 'correspondence_city', 'invoice_name', 'invoice_receiver', 'invoice_email', 'invoice_street', 'invoice_building', 'invoice_locale', 'invoice_postcode', 'invoice_city', 'invoice_tax_number', 'invoice_country_prefix', 'markup_products', 'markup_services', 'markup_setup_services', 'markup_shipping'],
      loadedKey: 'loadedData',
      errorKey: 'loadError',
      overrideID: idToLoad,
      successAction: () => {
        this.loadAdditionalData();
        this.loadAddresses();
        this.loadContactPersons();
      }
    });

    this.$bus.$on('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$on('customer-edit-contact-person-popup-close', this.closeContactPersonEditPopup);
    this.$bus.$on('customer-reload-contact-persons', this.loadContactPersons);
    this.$bus.$on('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$on('customer-edit-address-popup-close', this.closeAddressEditPopup);
    this.$bus.$on('customer-reload-addresses', this.loadAddresses);
  },
  methods: {
    setCustomerLogo (event) {
      this.logo = event.target.files[0];
    },
    removeCustomerLogo () {
      this.logo_url = '';
      this.logo = '';
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError',
        successAction: () => {
          this.languages.reverse();

          if (this.language_id === null) {
            this.language_id = this.languages[0].id;
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/shippers/items',
        loadedKey: 'loadedShippers',
        outputKey: 'shippers',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError',
        successAction: () => {
          if (this.currency_id === null) {
            this.currency_id = this.currencies[0].id;
          }
        }
      });
    },
    loadAddresses () {
      this.loadedAddresses = false;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/customers/addresses/items',
        loadedKey: 'loadedAddresses',
        outputKey: 'addresses',
        errorKey: 'loadAddressError'
      });
    },
    loadContactPersons () {
      this.loadedContactPersons = false;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/customers/contacts/items',
        loadedKey: 'loadedContactPersons',
        outputKey: 'contactPersons',
        errorKey: 'loadContactPersonsError'
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['customer-edit-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;
      let formDataToSend = {
        name: this.name,
        email: this.email,
        phone: this.phone,
        country_id: this.country_id,
        currency_id: this.currency_id,
        language_id: this.language_id,
        shipper_id: this.shipper_id,
        markup_products: this.markup_products,
        markup_services: this.markup_services,
        markup_setup_services: this.markup_setup_services,
        markup_shipping: this.markup_shipping,
        correspondence_country_id: this.correspondence_country_id,
        correspondence_name: this.correspondence_name,
        correspondence_company_name: this.correspondence_company_name,
        correspondence_street: this.correspondence_street,
        correspondence_building: this.correspondence_building,
        correspondence_locale: this.correspondence_locale,
        correspondence_postcode: this.correspondence_postcode,
        correspondence_city: this.correspondence_city
      };

      if (this.logo) {
        formDataToSend.logo = this.logo;
        imageFields = ['logo'];
      }

      let endpointUrl = '/customer-panel/customers/update';

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('customer.editClientSuccessTitle'),
        successText: this.$t('customer.editClientSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.editClientErrorText'),
        successAction: () => {
          let idToLoad = 0;
          let loadEndpointUrl = '/customer-panel/customers/item';

          if (this.language_id) {
            this.$store.commit('setUserLang', parseInt(this.language_id, 10));
            this.$store.commit('setUserLangCode', this.languageCode);
          }

          if (this.$store.getters.getUserLangCode !== this.languageCode) {
            window.localStorage.setItem('headwear-crm-ui-language', this.languageCode.replace(/[^a-z]/gmi, ''));
            window.location.reload();
          }

          FormUtils.loadItemData(this, {
            endpointBase: loadEndpointUrl,
            overrideID: idToLoad,
            instanceFields: ['logo', 'logo_url'],
            loadedKey: 'loadedData',
            errorKey: 'loadError'
          });
          this.loadAddresses();
          this.loadContactPersons();
        }
      }, imageFields);
    },
    /**
     * Addresses
     */
    addAddress () {
      this.showAddressAddPopup = true;
    },
    editAddress (address) {
      this.selectedAddress = JSON.parse(JSON.stringify(address));
      this.showAddressEditPopup = true;
    },
    removeAddress (addressID) {
      this.removeAddressInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customer-panel/customers/addresses/delete/',
        id: addressID,
        successTitle: this.$t('customer.deleteAddressSuccessTitle'),
        successText: this.$t('customer.deleteAddressSuccessText'),
        confirmTitle: this.$t('customer.deleteAddressConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.deleteAddressErrorText'),
        successAction: () => {
          this.removeAddressInProgress = false;
          this.loadAddresses();
        },
        cancelAction: () => {
          this.removeAddressInProgress = false;
        }
      });
    },
    closeAddressAddPopup () {
      this.showAddressAddPopup = false;
    },
    closeAddressEditPopup () {
      this.showAddressEditPopup = false;
    },
    /**
     * Contact persons
     */
    addContactPerson () {
      this.showContactPersonAddPopup = true;
    },
    editContactPerson (contactPerson) {
      this.selectedContactPerson = JSON.parse(JSON.stringify(contactPerson));
      this.showContactPersonEditPopup = true;
    },
    removeContactPerson (contactPersonID) {
      this.removeContactPersonInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customer-panel/customers/contacts/delete/',
        id: contactPersonID,
        successTitle: this.$t('customer.deleteContactPersonSuccessTitle'),
        successText: this.$t('customer.deleteContactPersonSuccessText'),
        confirmTitle: this.$t('customer.deleteContactPersonConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.deleteContactPersonErrorText'),
        successAction: () => {
          this.removeContactPersonInProgress = false;
          this.loadContactPersons();
        }
      });
    },
    closeContactPersonAddPopup () {
      this.showContactPersonAddPopup = false;
    },
    closeContactPersonEditPopup () {
      this.showContactPersonEditPopup = false;
    },
    getCountry (countryID) {
      return CommonGetters.getCountryById(countryID, this.countries);
    },
    /**
     * Password change
     */
    validatePasswordChangeForm () {
      FormUtils.validate(this.$refs['customer-password-change'], this.changePassword);
    },
    changePassword () {
      this.passwordChangeInProgress = true;
      let formDataToSend = {
        password: this.password,
        password_confirmation: this.passwordConfirmation
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customer-panel/users/change-password',
        formData: formDataToSend,
        successTitle: this.$t('customer.editPasswordSuccessTitle'),
        successText: this.$t('customer.editPasswordSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.editPasswordErrorText'),
        successAction: () => {
          this.passwordChangeInProgress = false;
        },
        failAction: () => {
          this.passwordChangeInProgress = false;
        }
      });
    },
    countryChange () {
      Vue.nextTick(() => {
        let countryData = this.countries.filter(country => country.id === this.country_id);

        if (countryData[0]) {
          let currencyID = countryData[0].default_currency_id;
          let languageID = countryData[0].default_language_id;

          if (currencyID) {
            Vue.set(this, 'currency_id', currencyID);
          } else {
            Vue.set(this, 'currency_id', null);
          }

          if (languageID) {
            Vue.set(this, 'language_id', languageID);
          } else {
            Vue.set(this, 'language_id', null);
          }
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$off('customer-edit-contact-person-popup-close', this.closeContactPersonEditPopup);
    this.$bus.$off('customer-reload-contact-persons', this.loadContactPersons);
    this.$bus.$off('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$off('customer-edit-address-popup-close', this.closeAddressEditPopup);
    this.$bus.$off('customer-reload-addresses', this.loadAddresses);
  }
};
</script>

<style lang="scss" scoped>
.customer-logo {
  img {
    height: auto;
    width: 120px;
  }
}
</style>
